import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  H5,
  Icon,
  Loading,
  M,
  PopupMenu,
  TextButton,
  Tooltip,
} from 'design-system-web';
import { Alert } from 'components/lds';
import {
  createTargetTimeRule,
  deleteTargetTimeRule,
  searchTargetTimeRules,
  updateTargetTimeRule,
} from 'graphql-queries';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { Channel } from 'lane-shared/types/ChannelType';
import { UserType } from 'lane-shared/types/User';

import {
  TargetTimesCreateModal,
  type DropdownOption,
} from './TargetTimesCreateModal';
import { formatTargetDurationSeconds } from './targetTimesFormat';
import {
  parseTargetTimeDurationFields,
  parseTargetTimeModalSubmit,
} from './targetTimesModalSubmit';
import styles from './TargetTimesTab.scss';

const LIST_PAGINATION = { start: 0, perPage: 500 };

/** Dropdown value: create one rule per issue in the selected category. */
const ALL_ISSUES_VALUE = '__TARGET_TIMES_ALL_ISSUES__';

/** Dropdown value: retry every issue that failed on the last partial submit. */
const FAILED_RETRY_BATCH_VALUE = '__TARGET_TIMES_FAILED_RETRY_BATCH__';

async function createTargetTimeRulesSequential(
  createRule: (opts: {
    variables: {
      input: {
        channelId: string;
        category: string;
        issue: string;
        targetResponseSeconds: number;
        targetCompletionSeconds: number;
      };
    };
  }) => Promise<unknown>,
  params: {
    channelId: string;
    category: string;
    issues: string[];
    targetResponseSeconds: number;
    targetCompletionSeconds: number;
  }
): Promise<{
  successCount: number;
  failures: string[];
  failedIssues: string[];
}> {
  const failures: string[] = [];
  const failedIssues: string[] = [];
  let successCount = 0;

  for (const issue of params.issues) {
    try {
      await createRule({
        variables: {
          input: {
            channelId: params.channelId,
            category: params.category,
            issue,
            targetResponseSeconds: params.targetResponseSeconds,
            targetCompletionSeconds: params.targetCompletionSeconds,
          },
        },
      });
      successCount += 1;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);

      failedIssues.push(issue);
      failures.push(`${issue}: ${message}`);
    }
  }

  return { successCount, failures, failedIssues };
}

function showCreateResultToast(
  t: TFunction,
  successCount: number,
  failuresLength: number
) {
  if (successCount === 0) {
    return;
  }

  if (successCount === 1 && failuresLength === 0) {
    window.Toast.show(
      t('web.admin.serviceRequest.settings.targetTimes.toast.created')
    );

    return;
  }

  if (failuresLength === 0) {
    window.Toast.show(
      t('web.admin.serviceRequest.settings.targetTimes.toast.createdMany', {
        count: successCount,
      })
    );

    return;
  }

  window.Toast.show(
    t('web.admin.serviceRequest.settings.targetTimes.toast.createdPartial', {
      ok: successCount,
      failed: failuresLength,
    })
  );
}

type TargetTimeSubmitFlowParams = {
  createRule: (opts: {
    variables: {
      input: {
        channelId: string;
        category: string;
        issue: string;
        targetResponseSeconds: number;
        targetCompletionSeconds: number;
      };
    };
  }) => Promise<unknown>;
  refetch: (vars: {
    channelId: string;
    pagination: { start: number; perPage: number };
  }) => Promise<unknown>;
  listVariables: {
    channelId: string;
    pagination: { start: number; perPage: number };
  };
  channelId: string;
  category: string;
  issues: string[];
  targetResponseSeconds: number;
  targetCompletionSeconds: number;
  t: TFunction;
  setFormError: (msg: string | null) => void;
  handleCloseModal: () => void;
};

type TargetTimeUpdateRule = (opts: {
  variables: {
    input: {
      channelId: string;
      targetTimeRuleId: string;
      targetResponseSeconds: number;
      targetCompletionSeconds: number;
    };
  };
}) => Promise<unknown>;

async function runTargetTimeSubmitFlow(
  params: TargetTimeSubmitFlowParams
): Promise<{
  successCount: number;
  failures: string[];
  failedIssues: string[];
  refetchFailed: boolean;
}> {
  const { successCount, failures, failedIssues } =
    await createTargetTimeRulesSequential(params.createRule, {
      channelId: params.channelId,
      category: params.category,
      issues: params.issues,
      targetResponseSeconds: params.targetResponseSeconds,
      targetCompletionSeconds: params.targetCompletionSeconds,
    });

  let refetchFailed = false;

  try {
    await params.refetch(params.listVariables);
  } catch {
    refetchFailed = true;
  }

  if (failures.length > 0) {
    params.setFormError(
      params.t(
        'web.admin.serviceRequest.settings.targetTimes.form.validation.createFailed',
        {
          details: failures.join(' '),
        }
      )
    );
  } else if (refetchFailed) {
    params.setFormError(
      params.t(
        'web.admin.serviceRequest.settings.targetTimes.form.validation.refreshFailed'
      )
    );
  }

  showCreateResultToast(params.t, successCount, failures.length);

  if (failures.length === 0 && !refetchFailed) {
    params.handleCloseModal();
  }

  return { successCount, failures, failedIssues, refetchFailed };
}

function syncPendingRetryIssuesAfterSubmit(options: {
  flowResult: {
    failedIssues: string[];
    refetchFailed: boolean;
    successCount: number;
  };
  setPendingRetryIssues: (issues: string[] | null) => void;
  setIssueValue: (value: string) => void;
  setPendingRefetchRetry: (value: boolean) => void;
  failedRetryBatchSentinel: string;
}): void {
  const {
    flowResult,
    setPendingRetryIssues,
    setIssueValue,
    setPendingRefetchRetry,
    failedRetryBatchSentinel,
  } = options;

  if (flowResult.failedIssues.length > 0) {
    setPendingRetryIssues(flowResult.failedIssues);
    setPendingRefetchRetry(false);

    if (flowResult.failedIssues.length === 1) {
      setIssueValue(flowResult.failedIssues[0]);
    } else {
      setIssueValue(failedRetryBatchSentinel);
    }

    return;
  }

  setPendingRetryIssues(null);

  if (flowResult.refetchFailed && flowResult.successCount > 0) {
    setPendingRefetchRetry(true);
  } else {
    setPendingRefetchRetry(false);
  }
}

type TargetTimesModalSubmitContext = {
  submitGuardRef: React.MutableRefObject<boolean>;
  pendingRefetchRetry: boolean;
  categoryValue: string | null;
  issueValue: string;
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
  categories: WorkOrderModuleCategoriesSettings[];
  pendingRetryIssues: string[] | null;
  issuesWithExistingRulesForCategory: string[];
  failedRetryBatchSentinel: string;
  channelId: string;
  createRule: TargetTimeSubmitFlowParams['createRule'];
  refetch: TargetTimeSubmitFlowParams['refetch'];
  listVariables: TargetTimeSubmitFlowParams['listVariables'];
  setFormError: (msg: string | null) => void;
  handleCloseModal: () => void;
  setSubmitInFlight: (value: boolean) => void;
  setPendingRetryIssues: (issues: string[] | null) => void;
  setPendingRefetchRetry: (value: boolean) => void;
  setIssueValue: (value: string) => void;
  t: TFunction;
};

async function runRefetchOnlyModalRetry(
  ctx: TargetTimesModalSubmitContext
): Promise<void> {
  ctx.submitGuardRef.current = true;

  try {
    ctx.setSubmitInFlight(true);
    ctx.setFormError(null);

    try {
      await ctx.refetch(ctx.listVariables);
    } catch {
      ctx.setFormError(
        ctx.t(
          'web.admin.serviceRequest.settings.targetTimes.form.validation.refreshFailed'
        )
      );

      return;
    }

    ctx.setPendingRefetchRetry(false);
    ctx.handleCloseModal();
  } finally {
    ctx.submitGuardRef.current = false;
    ctx.setSubmitInFlight(false);
  }
}

async function runTargetTimesModalSubmit(
  ctx: TargetTimesModalSubmitContext
): Promise<void> {
  if (ctx.submitGuardRef.current) {
    return;
  }

  if (ctx.pendingRefetchRetry) {
    await runRefetchOnlyModalRetry(ctx);

    return;
  }

  ctx.setFormError(null);

  const parsedSubmit = parseTargetTimeModalSubmit({
    categoryValue: ctx.categoryValue,
    issueValue: ctx.issueValue,
    respHours: ctx.respHours,
    respMinutes: ctx.respMinutes,
    compHours: ctx.compHours,
    compMinutes: ctx.compMinutes,
    categories: ctx.categories,
    allIssuesSentinel: ALL_ISSUES_VALUE,
    issuesWithExistingRules: ctx.issuesWithExistingRulesForCategory,
    failedRetryBatchSentinel: ctx.failedRetryBatchSentinel,
    pendingRetryIssues: ctx.pendingRetryIssues,
    t: ctx.t,
  });

  if (!parsedSubmit.ok) {
    ctx.setFormError(parsedSubmit.error);

    return;
  }

  const issuesToCreate = parsedSubmit.issues;

  ctx.submitGuardRef.current = true;

  try {
    ctx.setSubmitInFlight(true);

    const flowResult = await runTargetTimeSubmitFlow({
      createRule: ctx.createRule,
      refetch: ctx.refetch,
      listVariables: ctx.listVariables,
      channelId: ctx.channelId,
      category: parsedSubmit.category,
      issues: issuesToCreate,
      targetResponseSeconds: parsedSubmit.targetResponseSeconds,
      targetCompletionSeconds: parsedSubmit.targetCompletionSeconds,
      t: ctx.t,
      setFormError: ctx.setFormError,
      handleCloseModal: ctx.handleCloseModal,
    });

    syncPendingRetryIssuesAfterSubmit({
      flowResult,
      setPendingRetryIssues: ctx.setPendingRetryIssues,
      setIssueValue: ctx.setIssueValue,
      setPendingRefetchRetry: ctx.setPendingRefetchRetry,
      failedRetryBatchSentinel: ctx.failedRetryBatchSentinel,
    });
  } catch (e: unknown) {
    ctx.setFormError(e instanceof Error ? e.message : String(e));
  } finally {
    ctx.submitGuardRef.current = false;
    ctx.setSubmitInFlight(false);
  }
}

type TargetTimesEditModalSubmitContext = {
  submitGuardRef: React.MutableRefObject<boolean>;
  channelId: string;
  targetTimeRuleId: string;
  durations: TargetTimeDurationFields;
  updateRule: TargetTimeUpdateRule;
  refetch: TargetTimeSubmitFlowParams['refetch'];
  listVariables: TargetTimeSubmitFlowParams['listVariables'];
  setFormError: (msg: string | null) => void;
  handleCloseModal: () => void;
  setSubmitInFlight: (value: boolean) => void;
  t: TFunction;
};

async function runTargetTimesEditModalSubmit(
  ctx: TargetTimesEditModalSubmitContext
): Promise<void> {
  if (ctx.submitGuardRef.current) {
    return;
  }

  ctx.setFormError(null);
  const parsed = parseTargetTimeDurationFields(ctx.durations);

  if (!parsed.ok) {
    ctx.setFormError(ctx.t(parsed.errorKey));

    return;
  }

  ctx.submitGuardRef.current = true;

  try {
    ctx.setSubmitInFlight(true);
    await ctx.updateRule({
      variables: {
        input: {
          channelId: ctx.channelId,
          targetTimeRuleId: ctx.targetTimeRuleId,
          targetResponseSeconds: parsed.targetResponseSeconds,
          targetCompletionSeconds: parsed.targetCompletionSeconds,
        },
      },
    });
    window.Toast.show(
      ctx.t('web.admin.serviceRequest.settings.targetTimes.toast.updated')
    );

    try {
      await ctx.refetch(ctx.listVariables);
    } catch {
      window.Toast.show(
        ctx.t('web.admin.serviceRequest.settings.targetTimes.edit.refreshError')
      );
    }

    ctx.handleCloseModal();
  } catch {
    ctx.setFormError(
      ctx.t('web.admin.serviceRequest.settings.targetTimes.edit.error')
    );
  } finally {
    ctx.submitGuardRef.current = false;
    ctx.setSubmitInFlight(false);
  }
}

type Props = {
  channel: Channel;
  user: UserType;
  categories: WorkOrderModuleCategoriesSettings[];
};

type RuleRow = {
  id: string;
  category: string;
  issue: string;
  targetResponseSeconds: number;
  targetCompletionSeconds: number;
};

type TargetTimeDurationFields = {
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
};

const INITIAL_TARGET_TIME_DURATIONS: TargetTimeDurationFields = {
  respHours: '0',
  respMinutes: '0',
  compHours: '0',
  compMinutes: '0',
};

function targetTimeDurationFieldsFromSeconds(
  responseSeconds: number,
  completionSeconds: number
): TargetTimeDurationFields {
  const toFields = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));

    return {
      hours: String(Math.floor(safeSeconds / 3600)),
      minutes: String(Math.floor((safeSeconds % 3600) / 60)),
    };
  };

  const response = toFields(responseSeconds);
  const completion = toFields(completionSeconds);

  return {
    respHours: response.hours,
    respMinutes: response.minutes,
    compHours: completion.hours,
    compMinutes: completion.minutes,
  };
}

function targetTimesModalCategoryItems(
  editingRule: RuleRow | null,
  categoryItems: DropdownOption[]
): DropdownOption[] {
  return editingRule
    ? [{ label: editingRule.category, value: editingRule.category }]
    : categoryItems;
}

function targetTimesModalIssueItems(
  editingRule: RuleRow | null,
  issueItems: DropdownOption[]
): DropdownOption[] {
  return editingRule
    ? [{ label: editingRule.issue, value: editingRule.issue }]
    : issueItems;
}

function targetTimesModalCopy(editingRule: RuleRow | null): {
  titleKey: string;
  submitLabelKey: string;
} {
  if (editingRule) {
    return {
      titleKey: 'web.admin.serviceRequest.settings.targetTimes.edit.modalTitle',
      submitLabelKey: 'web.admin.serviceRequest.settings.targetTimes.edit.submit',
    };
  }

  return {
    titleKey: 'web.admin.serviceRequest.settings.targetTimes.modalTitle',
    submitLabelKey: 'web.admin.serviceRequest.settings.targetTimes.form.submit',
  };
}

function targetTimesModalConfig(input: {
  editingRule: RuleRow | null;
  categoryItems: DropdownOption[];
  issueItems: DropdownOption[];
  categoryValue: string | null;
}) {
  const isEditing = Boolean(input.editingRule);

  return {
    ...targetTimesModalCopy(input.editingRule),
    categoryItems: targetTimesModalCategoryItems(
      input.editingRule,
      input.categoryItems
    ),
    issueItems: targetTimesModalIssueItems(input.editingRule, input.issueItems),
    categoryDisabled: isEditing || input.categoryItems.length === 0,
    issueDisabled:
      isEditing || !input.categoryValue || input.issueItems.length === 0,
  };
}

function TargetTimesRuleTable({
  channelId,
  rules,
  refetchRules,
  onEdit,
}: {
  channelId: string;
  rules: RuleRow[];
  refetchRules: () => Promise<unknown>;
  onEdit: (rule: RuleRow) => void;
}) {
  const { t } = useTranslation();
  const [deleteRule, { loading: deleteLoading }] =
    useMutation(deleteTargetTimeRule);

  const headerWithTooltip = (labelKey: string, tooltipKey: string) => (
    <span className={styles.thLabel}>
      {t(labelKey)}
      <Tooltip TooltipComponent={t(tooltipKey)}>
        <button
          type="button"
          className={styles.infoTrigger}
          aria-label={t(tooltipKey)}
        >
          <Icon name="info-circle" set="FontAwesome" type="far" />
        </button>
      </Tooltip>
    </span>
  );

  const confirmAndDelete = async (rule: RuleRow) => {
    try {
      await window.Alert.confirm({
        title: t(
          'web.admin.serviceRequest.settings.targetTimes.delete.confirmTitle'
        ),
        message: t(
          'web.admin.serviceRequest.settings.targetTimes.delete.confirmMessage'
        ),
        confirmText: t(
          'web.admin.serviceRequest.settings.targetTimes.delete.confirmButton'
        ),
        cancelText: t(
          'web.admin.serviceRequest.settings.targetTimes.delete.cancelButton'
        ),
      });
    } catch {
      return;
    }

    try {
      await deleteRule({
        variables: {
          input: {
            channelId,
            targetTimeRuleId: rule.id,
          },
        },
      });
    } catch {
      window.Toast.show(
        t('web.admin.serviceRequest.settings.targetTimes.delete.error')
      );

      return;
    }

    window.Toast.show(
      t('web.admin.serviceRequest.settings.targetTimes.toast.deleted')
    );

    try {
      await refetchRules();
    } catch {
      window.Toast.show(
        t('web.admin.serviceRequest.settings.targetTimes.delete.refreshError')
      );
    }
  };

  if (rules.length === 0) {
    return <M>{t('web.admin.serviceRequest.settings.targetTimes.empty')}</M>;
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>
              {t(
                'web.admin.serviceRequest.settings.targetTimes.table.category'
              )}
            </th>
            <th className={styles.th}>
              {t('web.admin.serviceRequest.settings.targetTimes.table.issue')}
            </th>
            <th className={styles.th}>
              {headerWithTooltip(
                'web.admin.serviceRequest.settings.targetTimes.table.responseTime',
                'web.admin.serviceRequest.settings.targetTimes.tooltip.response'
              )}
            </th>
            <th className={styles.th}>
              {headerWithTooltip(
                'web.admin.serviceRequest.settings.targetTimes.table.completionTime',
                'web.admin.serviceRequest.settings.targetTimes.tooltip.completion'
              )}
            </th>
            <th className={`${styles.th} ${styles.thActions}`}>
              {t('web.admin.serviceRequest.settings.targetTimes.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rules.map(rule => (
            <tr key={rule.id}>
              <td className={styles.td}>{rule.category}</td>
              <td className={styles.td}>{rule.issue}</td>
              <td className={styles.td}>
                {formatTargetDurationSeconds(rule.targetResponseSeconds)}
              </td>
              <td className={styles.td}>
                {formatTargetDurationSeconds(rule.targetCompletionSeconds)}
              </td>
              <td className={`${styles.td} ${styles.tdActions}`}>
                <PopupMenu
                  trigger={
                    <TextButton
                      padding="none"
                      iconName="angle-down"
                      testId={`target-times-actions-${rule.id}`}
                      disabled={deleteLoading}
                    >
                      {t(
                        'web.admin.serviceRequest.settings.targetTimes.table.actionsMenu'
                      )}
                    </TextButton>
                  }
                  placement="bottom-end"
                  items={[
                    {
                      label: t(
                        'web.admin.serviceRequest.settings.targetTimes.edit.menuLabel'
                      ),
                      testId: `target-times-edit-${rule.id}`,
                      onSelect: () => {
                        onEdit(rule);
                      },
                    },
                    {
                      label: t(
                        'web.admin.serviceRequest.settings.targetTimes.delete.menuLabel'
                      ),
                      variant: 'critical',
                      testId: `target-times-delete-${rule.id}`,
                      onSelect: () => {
                        confirmAndDelete(rule);
                      },
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type TargetTimesFormOptionsInput = {
  categories: WorkOrderModuleCategoriesSettings[];
  categoryValue: string | null;
  rules: RuleRow[];
  pendingRetryIssues: string[] | null;
  t: TFunction;
};

function countCreatableIssuesForCategory(
  category: WorkOrderModuleCategoriesSettings,
  rules: RuleRow[]
): number {
  const name = category.firendlyName ?? '';

  if (name.trim() === '') {
    return 0;
  }

  const issueStrings = (category.issues ?? []).filter(
    i => (i ?? '').trim() !== ''
  );

  if (issueStrings.length === 0) {
    return 0;
  }

  const existingIssues = new Set(
    rules.filter(r => r.category === name).map(r => r.issue)
  );

  return issueStrings.filter(issue => !existingIssues.has(issue)).length;
}

function useTargetTimesFormOptions({
  categories,
  categoryValue,
  rules,
  pendingRetryIssues,
  t,
}: TargetTimesFormOptionsInput) {
  const categoryItems = useMemo(
    () =>
      categories
        .filter(c => (c.firendlyName ?? '').trim() !== '')
        .filter(c => countCreatableIssuesForCategory(c, rules) > 0)
        .map(c => ({
          label: c.firendlyName,
          value: c.firendlyName,
        })),
    [categories, rules]
  );

  const selectedCategory = useMemo(
    () =>
      categoryValue
        ? categories.find(c => c.firendlyName === categoryValue)
        : undefined,
    [categories, categoryValue]
  );

  const issuesWithExistingRulesForCategory = useMemo(() => {
    if (!categoryValue) {
      return [];
    }

    return rules.filter(r => r.category === categoryValue).map(r => r.issue);
  }, [rules, categoryValue]);

  const issueItems = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    const issueStrings = (selectedCategory.issues ?? []).filter(
      i => (i ?? '').trim() !== ''
    );
    const existingSet = new Set(issuesWithExistingRulesForCategory);
    const creatableIssues = issueStrings.filter(
      issue => !existingSet.has(issue)
    );
    const pending = (pendingRetryIssues ?? []).filter(Boolean);

    if (pending.length > 1) {
      const stillFailed = pending.filter(issue => !existingSet.has(issue));

      if (stillFailed.length === 0) {
        return [];
      }

      return [
        {
          label: t(
            'web.admin.serviceRequest.settings.targetTimes.form.retryFailedIssues',
            { count: stillFailed.length }
          ),
          value: FAILED_RETRY_BATCH_VALUE,
        },
        ...stillFailed.map(issue => ({ label: issue, value: issue })),
      ];
    }

    if (creatableIssues.length === 0) {
      return [];
    }

    if (creatableIssues.length === 1) {
      const only = creatableIssues[0];

      return [{ label: only, value: only }];
    }

    return [
      {
        label: t(
          'web.admin.serviceRequest.settings.targetTimes.form.allIssues'
        ),
        value: ALL_ISSUES_VALUE,
      },
      ...creatableIssues.map(issue => ({ label: issue, value: issue })),
    ];
  }, [
    selectedCategory,
    issuesWithExistingRulesForCategory,
    pendingRetryIssues,
    t,
  ]);

  return { categoryItems, issueItems, issuesWithExistingRulesForCategory };
}

type IssueDropdownAlignmentInput = {
  modalOpen: boolean;
  categoryValue: string | null;
  issueItems: { value: string }[];
  issueValue: string;
  setIssueValue: (value: string) => void;
};

function useTargetTimesIssueDropdownAlignment(
  input: IssueDropdownAlignmentInput
) {
  const { modalOpen, categoryValue, issueItems, issueValue, setIssueValue } =
    input;

  useEffect(() => {
    if (!modalOpen || !categoryValue) {
      return;
    }

    const allowed = new Set(issueItems.map(item => item.value));

    if (allowed.size === 0) {
      return;
    }

    if (!allowed.has(issueValue)) {
      setIssueValue(issueItems[0].value);
    }
  }, [modalOpen, categoryValue, issueItems, issueValue, setIssueValue]);
}

type CategoryDropdownAlignmentInput = {
  modalOpen: boolean;
  categoryValue: string | null;
  categoryItems: { value: string }[];
  setCategoryValue: (value: string | null) => void;
  setIssueValue: (value: string) => void;
  setPendingRetryIssues: (issues: string[] | null) => void;
};

function useTargetTimesCategoryDropdownAlignment(
  input: CategoryDropdownAlignmentInput
) {
  const {
    modalOpen,
    categoryValue,
    categoryItems,
    setCategoryValue,
    setIssueValue,
    setPendingRetryIssues,
  } = input;

  useEffect(() => {
    if (!modalOpen || !categoryValue) {
      return;
    }

    const stillAllowed = categoryItems.some(c => c.value === categoryValue);

    if (!stillAllowed) {
      setCategoryValue(null);
      setIssueValue(ALL_ISSUES_VALUE);
      setPendingRetryIssues(null);
    }
  }, [
    modalOpen,
    categoryValue,
    categoryItems,
    setCategoryValue,
    setIssueValue,
    setPendingRetryIssues,
  ]);
}

function useTargetTimesDropdownAlignment(input: {
  isCreateModal: boolean;
  categoryValue: string | null;
  categoryItems: { value: string }[];
  issueItems: { value: string }[];
  issueValue: string;
  setCategoryValue: (value: string | null) => void;
  setIssueValue: (value: string) => void;
  setPendingRetryIssues: (issues: string[] | null) => void;
}) {
  useTargetTimesIssueDropdownAlignment({
    modalOpen: input.isCreateModal,
    categoryValue: input.categoryValue,
    issueItems: input.issueItems,
    issueValue: input.issueValue,
    setIssueValue: input.setIssueValue,
  });

  useTargetTimesCategoryDropdownAlignment({
    modalOpen: input.isCreateModal,
    categoryValue: input.categoryValue,
    categoryItems: input.categoryItems,
    setCategoryValue: input.setCategoryValue,
    setIssueValue: input.setIssueValue,
    setPendingRetryIssues: input.setPendingRetryIssues,
  });
}

function useTargetTimesRulesQuery(channelId: string) {
  const listVariables = useMemo(
    () => ({
      channelId,
      pagination: LIST_PAGINATION,
    }),
    [channelId]
  );

  const { data, loading, error, refetch } = useQuery(searchTargetTimeRules, {
    variables: listVariables,
    fetchPolicy: 'network-only',
  });

  const [createRule, { loading: saving }] = useMutation(createTargetTimeRule);
  const [updateRule, { loading: updating }] = useMutation(updateTargetTimeRule);

  const rules: RuleRow[] = data?.searchTargetTimeRules?.targetTimeRules ?? [];

  return {
    listVariables,
    data,
    loading,
    error,
    refetch,
    createRule,
    updateRule,
    saving: saving || updating,
    rules,
  };
}

function useTargetTimesFormChangeHandlers(
  clearPendingRetryIssuesOnly: () => void,
  setCategoryValue: (value: string | null) => void,
  setIssueValue: (value: string) => void
) {
  const onCategoryChange = useCallback(
    (value: string) => {
      setCategoryValue(value);
      setIssueValue(ALL_ISSUES_VALUE);
      clearPendingRetryIssuesOnly();
    },
    [clearPendingRetryIssuesOnly, setCategoryValue, setIssueValue]
  );
  const onIssueChange = useCallback(
    (value: string) => {
      setIssueValue(value);

      if (value !== FAILED_RETRY_BATCH_VALUE) {
        clearPendingRetryIssuesOnly();
      }
    },
    [clearPendingRetryIssuesOnly, setIssueValue]
  );

  return { onCategoryChange, onIssueChange };
}

function TargetTimesTabLoadingShell({
  loading,
  data,
  children,
}: {
  loading: boolean;
  data: unknown;
  children: React.ReactElement;
}) {
  if (loading && !data) {
    return <Loading />;
  }

  return children;
}

function useTargetTimesModalRetryState() {
  const [pendingRetryIssues, setPendingRetryIssues] = useState<string[] | null>(
    null
  );
  const [pendingRefetchRetry, setPendingRefetchRetry] = useState(false);

  const clearRetryFlags = useCallback(() => {
    setPendingRetryIssues(null);
    setPendingRefetchRetry(false);
  }, []);

  const clearPendingRetryIssuesOnly = useCallback(() => {
    setPendingRetryIssues(null);
  }, []);

  return {
    pendingRetryIssues,
    setPendingRetryIssues,
    pendingRefetchRetry,
    setPendingRefetchRetry,
    clearRetryFlags,
    clearPendingRetryIssuesOnly,
  };
}

export function TargetTimesTab({ channel, categories }: Props) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RuleRow | null>(null);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueValue, setIssueValue] = useState<string>(ALL_ISSUES_VALUE);
  const [durations, setDurations] = useState<TargetTimeDurationFields>({
    ...INITIAL_TARGET_TIME_DURATIONS,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const submitGuardRef = useRef(false);
  const {
    pendingRetryIssues,
    setPendingRetryIssues,
    pendingRefetchRetry,
    setPendingRefetchRetry,
    clearRetryFlags,
    clearPendingRetryIssuesOnly,
  } = useTargetTimesModalRetryState();

  const {
    listVariables,
    data,
    loading,
    error,
    refetch,
    createRule,
    updateRule,
    saving,
    rules,
  } = useTargetTimesRulesQuery(channel._id);

  const { categoryItems, issueItems, issuesWithExistingRulesForCategory } =
    useTargetTimesFormOptions({
      categories,
      categoryValue,
      rules,
      pendingRetryIssues,
      t,
    });

  useTargetTimesDropdownAlignment({
    isCreateModal: modalOpen && !editingRule,
    categoryValue,
    categoryItems,
    issueItems,
    issueValue,
    setCategoryValue,
    setIssueValue,
    setPendingRetryIssues,
  });

  const resetForm = useCallback(() => {
    setEditingRule(null);
    setCategoryValue(null);
    setIssueValue(ALL_ISSUES_VALUE);
    setDurations({ ...INITIAL_TARGET_TIME_DURATIONS });
    setFormError(null);
    clearRetryFlags();
    submitGuardRef.current = false;
  }, [clearRetryFlags]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    resetForm();
  }, [resetForm]);

  const handleOpenEditModal = useCallback((rule: RuleRow) => {
    setEditingRule(rule);
    setCategoryValue(rule.category);
    setIssueValue(rule.issue);
    setDurations(
      targetTimeDurationFieldsFromSeconds(
        rule.targetResponseSeconds,
        rule.targetCompletionSeconds
      )
    );
    setFormError(null);
    clearRetryFlags();
    submitGuardRef.current = false;
    setModalOpen(true);
  }, [clearRetryFlags]);

  const { onCategoryChange, onIssueChange } = useTargetTimesFormChangeHandlers(
    clearPendingRetryIssuesOnly,
    setCategoryValue,
    setIssueValue
  );

  const handleSubmit = useCallback(async () => {
    if (editingRule) {
      await runTargetTimesEditModalSubmit({
        submitGuardRef,
        channelId: channel._id,
        targetTimeRuleId: editingRule.id,
        durations,
        updateRule,
        refetch,
        listVariables,
        setFormError,
        handleCloseModal,
        setSubmitInFlight,
        t,
      });

      return;
    }

    await runTargetTimesModalSubmit({
      submitGuardRef,
      pendingRefetchRetry,
      categoryValue,
      issueValue,
      respHours: durations.respHours,
      respMinutes: durations.respMinutes,
      compHours: durations.compHours,
      compMinutes: durations.compMinutes,
      categories,
      pendingRetryIssues,
      issuesWithExistingRulesForCategory,
      failedRetryBatchSentinel: FAILED_RETRY_BATCH_VALUE,
      channelId: channel._id,
      createRule,
      refetch,
      listVariables,
      setFormError,
      handleCloseModal,
      setSubmitInFlight,
      setPendingRetryIssues,
      setPendingRefetchRetry,
      setIssueValue,
      t,
    });
  }, [
    categories,
    categoryValue,
    channel._id,
    createRule,
    editingRule,
    durations,
    handleCloseModal,
    issueValue,
    issuesWithExistingRulesForCategory,
    listVariables,
    pendingRefetchRetry,
    pendingRetryIssues,
    refetch,
    setPendingRefetchRetry,
    setPendingRetryIssues,
    t,
    updateRule,
  ]);

  const modalConfig = targetTimesModalConfig({
    editingRule,
    categoryItems,
    issueItems,
    categoryValue,
  });

  return (
    <TargetTimesTabLoadingShell loading={loading} data={data}>
      <Flex direction="column" gap={6} className={styles.targetTimesTab}>
        <div className={styles.sectionIntro}>
          <div className={styles.sectionHeader}>
            <H5 className={styles.sectionTitle}>
              {t`web.admin.serviceRequest.settings.tabs.targetTimes`}
            </H5>
            <Button
              variant="primary"
              size="large"
              className={styles.createButton}
              testId="target-times-create"
              onClick={() => {
                resetForm();
                setModalOpen(true);
              }}
            >
              {t`web.admin.serviceRequest.settings.targetTimes.addRule`}
            </Button>
          </div>
          <M className={styles.sectionDescription}>
            {t('web.admin.serviceRequest.settings.targetTimes.description')}
          </M>
        </div>

        {error ? (
          <Alert type="error">
            {t('web.admin.serviceRequest.settings.targetTimes.error.load')}
          </Alert>
        ) : null}

        <TargetTimesRuleTable
          channelId={channel._id}
          rules={rules}
          refetchRules={() => refetch(listVariables)}
          onEdit={handleOpenEditModal}
        />

        <TargetTimesCreateModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          formError={formError}
          categoryItems={modalConfig.categoryItems}
          categoryValue={categoryValue}
          onCategoryChange={onCategoryChange}
          categoryDisabled={modalConfig.categoryDisabled}
          issueItems={modalConfig.issueItems}
          issueDropdownValue={categoryValue ? issueValue : null}
          onIssueChange={onIssueChange}
          issueDisabled={modalConfig.issueDisabled}
          respHours={durations.respHours}
          onRespHoursChange={v =>
            setDurations(prev => ({ ...prev, respHours: v }))
          }
          respMinutes={durations.respMinutes}
          onRespMinutesChange={v =>
            setDurations(prev => ({ ...prev, respMinutes: v }))
          }
          compHours={durations.compHours}
          onCompHoursChange={v =>
            setDurations(prev => ({ ...prev, compHours: v }))
          }
          compMinutes={durations.compMinutes}
          onCompMinutesChange={v =>
            setDurations(prev => ({ ...prev, compMinutes: v }))
          }
          submitLoading={saving || submitInFlight}
          onSubmit={() => {
            handleSubmit();
          }}
          titleKey={modalConfig.titleKey}
          submitLabelKey={modalConfig.submitLabelKey}
        />
      </Flex>
    </TargetTimesTabLoadingShell>
  );
}
